import React, { useState, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField, Grid,
  LinearProgress, Card, CardContent, Accordion, AccordionSummary,
  AccordionDetails, List, ListItem, ListItemText, IconButton, Tabs, Tab
} from '@mui/material';
import {
  CloudUpload, CheckCircle, Error, Warning, ExpandMore,
  Edit, Save, Refresh, TableChart
} from '@mui/icons-material';
import BackendAPI from '../shared/api/backendApi';
import axios from 'axios';

const steps = ['Upload File', 'Analyze Data', 'Map Fields', 'Preview Data', 'Import'];

const DataImport = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('single'); // 'single' or 'universal'
  const [entityType, setEntityType] = useState('');
  const [dealerDataType, setDealerDataType] = useState(''); // 'dealer_master' or 'dealer_master_payments'
  const [purchaseDataType, setPurchaseDataType] = useState(''); // 'purchase_master' or 'purchase_items'
  const [salesDataType, setSalesDataType] = useState(''); // 'sales_master' or 'sales_items'
  const [companyId, setCompanyId] = useState('');
  const [userId, setUserId] = useState('1'); // Default fallback
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [universalAnalysis, setUniversalAnalysis] = useState(null);
  const [selectedEntityTab, setSelectedEntityTab] = useState(0);
  const fileInputRef = useRef();

  // New state for improved import experience
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Get company ID and user ID from user context (assuming it's stored in localStorage or context)
  React.useEffect(() => {
    // Try to get company ID and user ID from user data
    const userData = localStorage.getItem('userData');

    if (userData) {
      try {
        const user = JSON.parse(userData);

        // Set user ID
        if (user.id) {
          setUserId(user.id);
          localStorage.setItem('userId', user.id);
        }

        // Set company ID
        if (user.business && user.business.id) {
          setCompanyId(user.business.id);
          localStorage.setItem('companyId', user.business.id);
        } else if (user.company_id) {
          setCompanyId(user.company_id);
          localStorage.setItem('companyId', user.company_id);
        }
      } catch (e) {
        console.warn('Could not parse user data for company ID and user ID:', e);
      }
    }
  }, []);

  const [editMapping, setEditMapping] = useState(null);
  const [customMapping, setCustomMapping] = useState('');

  // Helper functions for improved import experience
  const startProgressTracking = () => {
    startTimeRef.current = Date.now();
    setTimeElapsed(0);
    setImportProgress(0);
    setImportStatus('Initializing import...');

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setTimeElapsed(elapsed);

      // Simulate progress based on time (0-90% over time, 90-100% near end)
      let progress = Math.min(90, (elapsed / 60) * 20); // 20% per minute, max 90%
      if (elapsed > 300) { // After 5 minutes, accelerate to 95%
        progress = 95;
      }
      setImportProgress(Math.round(progress));

      // Update status messages based on progress
      if (progress < 10) {
        setImportStatus('Preparing data for import...');
      } else if (progress < 30) {
        setImportStatus('Validating data and mappings...');
      } else if (progress < 50) {
        setImportStatus('Processing records...');
      } else if (progress < 70) {
        setImportStatus('Creating database entries...');
      } else if (progress < 90) {
        setImportStatus('Finalizing import...');
      } else {
        setImportStatus('Completing import...');
      }

      // Estimate remaining time (rough estimate)
      if (elapsed > 10) {
        const estimatedTotalTime = elapsed / (progress / 100);
        const remaining = Math.max(0, estimatedTotalTime - elapsed);
        setEstimatedTimeRemaining(Math.round(remaining));
      }
    }, 1000);
  };

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setImportProgress(100);
    setImportStatus('Import completed');
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setSuccess('');

    // Reset dealer data type if switching away from dealers
    if (entityType !== 'dealers') {
      setDealerDataType('');
    }

    // Reset purchase data type if switching away from purchase_invoices
    if (entityType !== 'purchase_invoices') {
      setPurchaseDataType('');
    }

    // Reset sales data type if switching away from sales_invoices
    if (entityType !== 'sales_invoices') {
      setSalesDataType('');
    }

    // Validate file
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Get auth token
      const authToken = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      // Test with direct axios call
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/import/validate-file`, formData, {
        headers,
        timeout: 10000
      });
      if (response.data.validation.isValid) {
        setSuccess('File validated successfully!');
      } else {
        setError(response.data.validation.error);
        setFile(null);
        return;
      }
    } catch (err) {
      setError('File validation failed: ' + err.response?.data?.error || err.message);
      setFile(null);
      return;
    }
  };

  const handleAnalyze = async () => {
    if (!file || !companyId) {
      setError('Please select a file and ensure company ID is set.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (importMode === 'universal') {
        // Analyze for universal import
        const formData = new FormData();
        formData.append('file', file);

        // Get auth token
        const authToken = localStorage.getItem('authToken');
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/import/universal/analyze`, formData, {
          headers,
          timeout: 10000
        });

        setUniversalAnalysis(response.data.analysis);
        setActiveStep(1); // Go to analysis step
      } else {
        // Single entity preview
        if (!entityType) {
          setError('Please select an entity type for single import.');
          return;
        }

        // Check dealer data type for dealer imports
        if (entityType === 'dealers' && !dealerDataType) {
          setError('Please select a dealer data type (Dealer Master or Dealer Master + Payments).');
          return;
        }

        // Check purchase data type for purchase imports
        if (entityType === 'purchase_invoices' && !purchaseDataType) {
          setError('Please select a purchase data type (Purchase Master or Purchase Items).');
          return;
        }

        // Check sales data type for sales imports
        if (entityType === 'sales_invoices' && !salesDataType) {
          setError('Please select a sales data type (Sales Master or Sales Items).');
          return;
        }

        // Get auth token
        const authToken = localStorage.getItem('authToken');
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        // Use dealer-specific API for dealer imports, purchase-specific API for purchase imports, general API for others
        if (entityType === 'dealers') {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/dealerImport/analyze`, formData, {
            headers,
            timeout: 10000
          });

          setPreviewData(response.data.data);
          setMappings(response.data.data.suggestedMappings || {});
          setActiveStep(1); // Go to analysis step
        } else if (entityType === 'purchase_invoices') {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/purchaseImport/analyze`, formData, {
            headers,
            timeout: 10000
          });

          setPreviewData(response.data.data);
          setMappings(response.data.data.suggestedMappings || {});
          setActiveStep(1); // Go to analysis step
        } else if (entityType === 'sales_invoices') {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/salesImport/analyze`, formData, {
            headers,
            timeout: 10000
          });

          setPreviewData(response.data.data);
          setMappings(response.data.data.suggestedMappings || {});
          setActiveStep(1); // Go to analysis step
        } else {
          // Use general import API for other entities
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entityType', entityType);
          formData.append('companyId', companyId);

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/import/preview`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10000
          });

          setPreviewData(response.data.data);
          setMappings(response.data.data.mapping.mappings);
          setActiveStep(1); // Go to analysis step
        }
      }
    } catch (err) {
      setError('Analysis failed: ' + err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (header, targetField) => {
    setMappings(prev => ({
      ...prev,
      [header]: {
        ...prev[header],
        targetField: targetField,
        fieldName: targetField // Support dealer format
      }
    }));
  };

  const handleEditMapping = (header) => {
    setEditMapping(header);
    // Support both formats: dealer format (fieldName) and general format (targetField)
    const currentMapping = mappings[header];
    setCustomMapping(currentMapping?.targetField || currentMapping?.fieldName || '');
  };

  const handleSaveMapping = () => {
    if (editMapping) {
      handleMappingChange(editMapping, customMapping);
      setEditMapping(null);
      setCustomMapping('');
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Start progress tracking
    startProgressTracking();

    try {
      if (importMode === 'universal') {
        // Execute universal import
        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', companyId);

        // Get auth token
        const authToken = localStorage.getItem('authToken');
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/import/universal`, formData, {
          headers,
          timeout: 600000 // 10 minute timeout for large imports
        });

        // Stop progress tracking and set completion
        stopProgressTracking();
        setSuccess('Universal import completed successfully!');
        setActiveStep(4);
      } else {
        // Execute single entity import
        if (entityType === 'dealers') {
          // Use dealer-specific import API
          const formData = new FormData();
          formData.append('file', file);
          formData.append('mappings', JSON.stringify(mappings));
          formData.append('dealerDataType', dealerDataType);

          const currentCompanyId = companyId;
          const currentUserId = userId;
          if (!currentCompanyId || !currentUserId) {
            setError('Company ID and User ID must be available to perform import. Please ensure you are logged in.');
            return;
          }

          formData.append('companyId', currentCompanyId);
          formData.append('userId', currentUserId);

          // Get auth token
          const authToken = localStorage.getItem('authToken');
          const headers = { 'Content-Type': 'multipart/form-data' };
          if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
          }

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/dealerImport/import`, formData, {
            headers,
            timeout: 600000 // 10 minute timeout for large imports
          });

          // Check if the import was actually successful
          if (response.data.success === false) {
            // Import failed - show detailed error message
            const errorMsg = response.data.error || 'Import failed';
            const failedCount = response.data.data?.statistics?.failed || 0;
            const importedCount = response.data.data?.statistics?.imported || 0;

            let detailedError = `${errorMsg}`;
            if (failedCount > 0) {
              detailedError += `\n${failedCount} rows failed validation.`;
              if (response.data.data?.errors?.length > 0) {
                detailedError += `\n\nValidation Errors:\n${response.data.data.errors.slice(0, 3).map(err =>
                  `Row ${err.row}: ${err.error}`
                ).join('\n')}`;
                if (response.data.data.errors.length > 3) {
                  detailedError += `\n...and ${response.data.data.errors.length - 3} more errors`;
                }
              }
            }

            setError(detailedError);
            return; // Don't proceed to success step
          }

          // Import successful
          const importedCount = response.data.data?.statistics?.imported || 0;
          const failedCount = response.data.data?.statistics?.failed || 0;
          const successMsg = `Dealer import completed: ${importedCount} dealers imported${failedCount > 0 ? `, ${failedCount} failed` : ''}`;

          setSuccess(successMsg);
          setActiveStep(4);
        } else if (entityType === 'purchase_invoices') {
          // Use purchase-specific import API
          const formData = new FormData();
          formData.append('file', file);
          formData.append('mappings', JSON.stringify(mappings));
          formData.append('purchaseDataType', purchaseDataType);

          const currentCompanyId = companyId;
          const currentUserId = userId;
          if (!currentCompanyId || !currentUserId) {
            setError('Company ID and User ID must be available to perform import. Please ensure you are logged in.');
            return;
          }

          formData.append('companyId', currentCompanyId);
          formData.append('userId', currentUserId);

          // Get auth token
          const authToken = localStorage.getItem('authToken');
          const headers = { 'Content-Type': 'multipart/form-data' };
          if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
          }

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/purchaseImport/import`, formData, {
            headers,
            timeout: 600000 // 10 minute timeout for large imports
          });

          // Check if the import was actually successful
          if (response.data.success === false) {
            // Import failed - show detailed error message
            const errorMsg = response.data.error || 'Import failed';
            const failedCount = response.data.data?.statistics?.failed || 0;
            const importedCount = response.data.data?.statistics?.imported || 0;

            let detailedError = `${errorMsg}`;
            if (failedCount > 0) {
              detailedError += `\n${failedCount} rows failed validation.`;
              if (response.data.data?.errors?.length > 0) {
                detailedError += `\n\nValidation Errors:\n${response.data.data.errors.slice(0, 3).map(err =>
                  `Row ${err.row}: ${err.error}`
                ).join('\n')}`;
                if (response.data.data.errors.length > 3) {
                  detailedError += `\n...and ${response.data.data.errors.length - 3} more errors`;
                }
              }
            }

            setError(detailedError);
            return; // Don't proceed to success step
          }

          // Import successful
          const importedCount = response.data.data?.statistics?.imported || 0;
          const failedCount = response.data.data?.statistics?.failed || 0;
          const successMsg = `Purchase import completed: ${importedCount} purchases imported${failedCount > 0 ? `, ${failedCount} failed` : ''}`;

          setSuccess(successMsg);
          setActiveStep(4);
        } else if (entityType === 'sales_invoices') {
          // Use sales-specific import API
          const formData = new FormData();
          formData.append('file', file);
          formData.append('mappings', JSON.stringify(mappings));
          formData.append('salesDataType', salesDataType);

          const currentCompanyId = companyId;
          const currentUserId = userId;
          if (!currentCompanyId || !currentUserId) {
            setError('Company ID and User ID must be available to perform import. Please ensure you are logged in.');
            return;
          }

          formData.append('companyId', currentCompanyId);
          formData.append('userId', currentUserId);

          // Get auth token
          const authToken = localStorage.getItem('authToken');
          const headers = { 'Content-Type': 'multipart/form-data' };
          if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
          }

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/salesImport/import`, formData, {
            headers,
            timeout: 600000 // 10 minute timeout for large imports
          });

          // Check if the import was actually successful
          if (response.data.success === false) {
            // Import failed - show detailed error message
            const errorMsg = response.data.error || 'Import failed';

            // Show the specific error that caused the failure
            setError(`Import Failed: ${errorMsg}\n\nNo data was imported. Please fix the data and try again.`);
            return; // Don't proceed to success step
          }

          // Import successful
          const results = response.data.results || {};
          const importedCount = results.imported || 0;
          const successMsg = `Sales import completed successfully: ${importedCount} sales imported`;

          // Stop progress tracking and set completion
          stopProgressTracking();
          // Stop progress tracking and set completion
          stopProgressTracking();
          // Stop progress tracking and set completion
          stopProgressTracking();
          setSuccess(successMsg);
          setActiveStep(4);
        } else {
          // Use general import API for other entities
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entityType', entityType);
          formData.append('mappings', JSON.stringify(mappings));
          formData.append('companyId', companyId);

          // Get auth token
          const authToken = localStorage.getItem('authToken');
          const headers = { 'Content-Type': 'multipart/form-data' };
          if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
          }

          const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/import/single`, formData, {
            headers,
            timeout: 600000 // 10 minute timeout for large imports
          });

          // Stop progress tracking and set completion
          stopProgressTracking();
          setSuccess('Import completed successfully!');
          setActiveStep(4);
        }
      }
    } catch (err) {
      // Stop progress tracking
      stopProgressTracking();

      let errorMessage = 'Import failed: ';

      if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }

      // Add additional context for timeout errors
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage += '\n\nThe import timed out. This may happen with very large files. Please try again or contact support if the issue persists.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getMappingStatus = (mapping) => {
    // Support both formats: dealer format (fieldName) and general format (targetField)
    const targetField = mapping?.targetField || mapping?.fieldName || mapping?.databaseField;
    const confidence = mapping?.confidence || 0;
    
    if (!targetField) return { color: 'error', icon: <Error />, text: 'Unmapped' };
    if (confidence > 0.8) return { color: 'success', icon: <CheckCircle />, text: 'High Confidence' };
    if (confidence > 0.6) return { color: 'warning', icon: <Warning />, text: 'Medium Confidence' };
    return { color: 'warning', icon: <Warning />, text: 'Low Confidence' };
  };

  const renderEntityPreview = (entityType, entityData) => {
    if (!entityData || !entityData.headers || entityData.headers.length === 0) {
      return (
        <Alert severity="warning">
          No preview data available for {entityType.replace('_', ' ').toUpperCase()}
        </Alert>
      );
    }

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Headers Detected
                </Typography>
                <Typography variant="h5">
                  {entityData.headers?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expected Records
                </Typography>
                <Typography variant="h5" color="primary">
                  {entityData.totalExpectedRecords || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Sample Rows
                </Typography>
                <Typography variant="h5" color="success.main">
                  {entityData.sampleData?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {entityData.sampleData && entityData.sampleData.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Sample Data Preview
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {entityData.headers.map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityData.sampleData.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {entityData.headers.map((header, colIndex) => (
                        <TableCell key={colIndex}>
                          {row[header] ? String(row[header]) : '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {entityData.expectedFields && entityData.expectedFields.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Expected Fields
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {entityData.expectedFields.map((field, index) => (
                <Chip
                  key={index}
                  label={field}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Your Data File
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Import Mode</InputLabel>
                  <Select
                    value={importMode}
                    onChange={(e) => setImportMode(e.target.value)}
                  >
                    <MenuItem value="single">Single Entity Import</MenuItem>
                    <MenuItem value="universal">Universal Multi-Entity Import</MenuItem>
                  </Select>
                </FormControl>

                {importMode === 'single' && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Entity Type</InputLabel>
                    <Select
                      value={entityType}
                      onChange={(e) => {
                            setEntityType(e.target.value);
                            setDealerDataType(''); // Reset dealer data type when entity changes
                            setPurchaseDataType(''); // Reset purchase data type when entity changes
                            setSalesDataType(''); // Reset sales data type when entity changes
                          }}
                    >
                      <MenuItem value="customers">Customers</MenuItem>
                      <MenuItem value="dealers">Dealers/Suppliers</MenuItem>
                      <MenuItem value="items">Items/Products</MenuItem>
                      <MenuItem value="sales_invoices">Sales Invoices</MenuItem>
                      <MenuItem value="purchase_invoices">Purchase Invoices</MenuItem>
                      <MenuItem value="expenses">Expenses</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {importMode === 'single' && entityType === 'dealers' && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Dealer Data Type</InputLabel>
                    <Select
                      value={dealerDataType}
                      onChange={(e) => setDealerDataType(e.target.value)}
                    >
                      <MenuItem value="dealer_master">Dealer Master</MenuItem>
                      <MenuItem value="dealer_master_payments">Dealer Master + Payments</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {importMode === 'single' && entityType === 'purchase_invoices' && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Purchase Data Type</InputLabel>
                    <Select
                      value={purchaseDataType}
                      onChange={(e) => setPurchaseDataType(e.target.value)}
                    >
                      <MenuItem value="purchase_master">Purchase Master</MenuItem>
                      <MenuItem value="purchase_items">Purchase Items</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {importMode === 'single' && entityType === 'sales_invoices' && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Sales Data Type</InputLabel>
                    <Select
                      value={salesDataType}
                      onChange={(e) => setSalesDataType(e.target.value)}
                    >
                      <MenuItem value="sales_master">Sales Master</MenuItem>
                      <MenuItem value="sales_items">Sales Items</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Company ID"
                  value={companyId}
                  margin="normal"
                  disabled
                  helperText="Automatically fetched from your profile"
                />

              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  border="2px dashed #ccc"
                  borderRadius={2}
                  p={3}
                  textAlign="center"
                  sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {file ? file.name : 'Click to upload file'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: Excel (.xlsx, .xls), CSV, PDF
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                  />
                </Box>

                {file && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleAnalyze}
                disabled={!file || !companyId || (importMode === 'single' && !entityType) || (importMode === 'single' && entityType === 'dealers' && !dealerDataType) || (importMode === 'single' && entityType === 'purchase_invoices' && !purchaseDataType) || (importMode === 'single' && entityType === 'sales_invoices' && !salesDataType) || loading}
              >
                {loading ? 'Analyzing...' : importMode === 'universal' ? 'Analyze File' : 'Preview Import'}
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {importMode === 'universal' ? 'Universal Import Analysis' : 'Single Entity Preview'}
            </Typography>

            {importMode === 'universal' && universalAnalysis && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Universal Import Analysis Complete!</strong>
                </Alert>

                <Typography variant="h6" gutterBottom>
                  Detected Entities in your file:
                </Typography>

                {Object.keys(universalAnalysis.detectedEntities).length > 0 ? (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {Object.entries(universalAnalysis.detectedEntities).map(([entityType, entityInfo]) => (
                      <Grid item xs={12} md={4} key={entityType}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="primary">
                              {entityType.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="body2">
                              Headers: {entityInfo.headers?.length || 0}
                            </Typography>
                            <Typography variant="body2">
                              Confidence: {Math.round(entityInfo.confidence * 100)}%
                            </Typography>
                            {universalAnalysis.entityPreviews && universalAnalysis.entityPreviews[entityType] && (
                              <Typography variant="body2">
                                Expected Records: {universalAnalysis.entityPreviews[entityType].totalExpectedRecords || 0}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="warning">
                    No entities were automatically detected. You may need to use Single Entity Import mode.
                  </Alert>
                )}

                {universalAnalysis.entityPreviews && Object.keys(universalAnalysis.entityPreviews).length > 0 && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      Entity-Specific Data Previews:
                    </Typography>
                    
                    <Tabs 
                      value={selectedEntityTab} 
                      onChange={(e, newValue) => setSelectedEntityTab(newValue)}
                      sx={{ mb: 2 }}
                    >
                      {Object.keys(universalAnalysis.entityPreviews).map((entityType, index) => (
                        <Tab 
                          key={entityType}
                          label={entityType.replace('_', ' ').toUpperCase()} 
                          icon={<TableChart />}
                          iconPosition="start"
                        />
                      ))}
                    </Tabs>

                    {Object.entries(universalAnalysis.entityPreviews).map(([entityType, entityData], index) => (
                      <Box key={entityType} hidden={selectedEntityTab !== index}>
                        {renderEntityPreview(entityType, entityData)}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {importMode === 'single' && previewData && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Preview generated for <strong>{entityType}</strong> entity
                  {entityType === 'dealers' && dealerDataType && (
                    <span> - <strong>{dealerDataType === 'dealer_master' ? 'Dealer Master' : 'Dealer Master + Payments'}</strong></span>
                  )}
                  {entityType === 'purchase_invoices' && purchaseDataType && (
                    <span> - <strong>{purchaseDataType === 'purchase_master' ? 'Purchase Master' : 'Purchase Items'}</strong></span>
                  )}
                  {entityType === 'sales_invoices' && salesDataType && (
                    <span> - <strong>{salesDataType === 'sales_master' ? 'Sales Master' : 'Sales Items'}</strong></span>
                  )}
                </Alert>

                <Typography variant="h6" gutterBottom>
                  File Analysis:
                </Typography>
                <Typography>Total Rows: {previewData.summary?.totalRows || previewData.totalRows || 0}</Typography>
                <Typography>Valid Rows: {previewData.summary?.validRows || previewData.totalRows || 0}</Typography>
                <Typography>Software Detected: {previewData.mapping?.softwareDetected || 'Unknown'}</Typography>
                {entityType === 'dealers' && dealerDataType && (
                  <Typography>Data Type: {dealerDataType === 'dealer_master' ? 'Dealer Master Only' : 'Dealer Master + Payments'}</Typography>
                )}
                {entityType === 'purchase_invoices' && purchaseDataType && (
                  <Typography>Data Type: {purchaseDataType === 'purchase_master' ? 'Purchase Master' : 'Purchase Items'}</Typography>
                )}
                {entityType === 'sales_invoices' && salesDataType && (
                  <Typography>Data Type: {salesDataType === 'sales_master' ? 'Sales Master' : 'Sales Items'}</Typography>
                )}
              </Box>
            )}

            <Box mt={3}>
              <Button onClick={() => setActiveStep(0)}>Back</Button>
              <Button variant="contained" onClick={() => setActiveStep(2)} sx={{ ml: 1 }}>
                Continue to Mapping
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Map Fields
            </Typography>

            {previewData?.mapping?.softwareDetected && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Detected software: <strong>{previewData.mapping.softwareDetected}</strong>
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Source Header</TableCell>
                    <TableCell>Mapped To</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(mappings).map(([header, mapping]) => {
                    const status = getMappingStatus(mapping);
                    return (
                      <TableRow key={header}>
                        <TableCell>{header}</TableCell>
                        <TableCell>
                          {editMapping === header ? (
                            <TextField
                              size="small"
                              value={customMapping}
                              onChange={(e) => setCustomMapping(e.target.value)}
                              fullWidth
                            />
                          ) : (
                            // Support both formats: dealer format (fieldName + databaseField) and general format (targetField)
                            mapping?.targetField ||
                            (mapping?.fieldName && mapping?.databaseField ? `${mapping.fieldName} (${mapping.databaseField})` : mapping?.fieldName) ||
                            'Not mapped'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={status.icon}
                            label={status.text}
                            color={status.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {editMapping === header ? (
                            <>
                              <IconButton size="small" onClick={handleSaveMapping}>
                                <Save />
                              </IconButton>
                              <IconButton size="small" onClick={() => setEditMapping(null)}>
                                <Refresh />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton size="small" onClick={() => handleEditMapping(header)}>
                              <Edit />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {previewData?.mapping?.suggestions?.length > 0 && (
              <Box mt={2}>
                <Typography variant="h6">Suggestions</Typography>
                <List>
                  {previewData.mapping.suggestions.map((suggestion, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Map "${suggestion.header}" to "${suggestion.suggestedField}"`}
                        secondary={`Confidence: ${(suggestion.confidence * 100).toFixed(0)}%`}
                      />
                      <Button
                        size="small"
                        onClick={() => handleMappingChange(suggestion.header, suggestion.suggestedField)}
                      >
                        Apply
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Box mt={3}>
              <Button onClick={() => setActiveStep(1)}>Back</Button>
              <Button variant="contained" onClick={() => setActiveStep(3)} sx={{ ml: 1 }}>
                Continue to Preview
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview Import Data
            </Typography>

            {importMode === 'universal' && universalAnalysis && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <strong>Universal Import Ready!</strong> The following entities will be imported automatically:
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {Object.entries(universalAnalysis.detectedEntities).map(([entityType, entityInfo]) => (
                    <Grid item xs={12} md={4} key={entityType}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {entityType.replace('_', ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="body2">
                            Headers: {entityInfo.headers?.length || 0}
                          </Typography>
                          <Typography variant="body2">
                            Expected Records: ~{Math.floor(universalAnalysis.totalRows * 0.8)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {universalAnalysis.entityPreviews && Object.keys(universalAnalysis.entityPreviews).length > 0 && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      Final Entity Previews:
                    </Typography>
                    
                    <Tabs 
                      value={selectedEntityTab} 
                      onChange={(e, newValue) => setSelectedEntityTab(newValue)}
                      sx={{ mb: 2 }}
                    >
                      {Object.keys(universalAnalysis.entityPreviews).map((entityType, index) => (
                        <Tab 
                          key={entityType}
                          label={entityType.replace('_', ' ').toUpperCase()} 
                          icon={<TableChart />}
                          iconPosition="start"
                        />
                      ))}
                    </Tabs>

                    {Object.entries(universalAnalysis.entityPreviews).map(([entityType, entityData], index) => (
                      <Box key={entityType} hidden={selectedEntityTab !== index}>
                        {renderEntityPreview(entityType, entityData)}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {importMode === 'single' && previewData && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{entityType}</strong> import preview
                  {entityType === 'dealers' && dealerDataType && (
                    <span> - <strong>{dealerDataType === 'dealer_master' ? 'Dealer Master' : 'Dealer Master + Payments'}</strong></span>
                  )}
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Total Rows
                        </Typography>
                        <Typography variant="h4">
                          {previewData?.summary?.totalRows || previewData?.totalRows || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Valid Rows
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {previewData?.summary?.validRows || previewData?.totalRows || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Invalid Rows
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {previewData?.summary?.invalidRows || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Warnings
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {previewData?.summary?.warningRows || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {entityType === 'dealers' && dealerDataType && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Dealer Import Configuration
                      </Typography>
                      <Typography>
                        <strong>Data Type:</strong> {dealerDataType === 'dealer_master' ? 'Dealer Master Only' : 'Dealer Master + Payments'}
                      </Typography>
                      {dealerDataType === 'dealer_master' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import only dealer information (name, address, contact details, etc.)
                        </Typography>
                      )}
                      {dealerDataType === 'dealer_master_payments' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import dealer information along with payment history and transaction details
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {entityType === 'purchase_invoices' && purchaseDataType && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Purchase Import Configuration
                      </Typography>
                      <Typography>
                        <strong>Data Type:</strong> {purchaseDataType === 'purchase_master' ? 'Purchase Master' : 'Purchase Items'}
                      </Typography>
                      {purchaseDataType === 'purchase_master' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import purchase invoice headers (supplier, date, total amount, etc.)
                        </Typography>
                      )}
                      {purchaseDataType === 'purchase_items' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import purchase invoices with detailed item lines (products, quantities, prices)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {entityType === 'sales_invoices' && salesDataType && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Sales Import Configuration
                      </Typography>
                      <Typography>
                        <strong>Data Type:</strong> {salesDataType === 'sales_master' ? 'Sales Master' : 'Sales Items'}
                      </Typography>
                      {salesDataType === 'sales_master' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import sales invoice headers (customer, date, total amount, etc.)
                        </Typography>
                      )}
                      {salesDataType === 'sales_items' && (
                        <Typography variant="body2" color="textSecondary">
                          This will import sales invoices with detailed item lines (products, quantities, prices, payments)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {previewData?.sampleData && previewData.sampleData.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Sample Transformed Data Preview
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {Object.keys(previewData.sampleData[0]).map((key, index) => (
                              <TableCell key={index}>{key}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.sampleData.map((row, index) => (
                            <TableRow key={index}>
                              {Object.values(row).map((value, i) => (
                                <TableCell key={i}>{String(value || '')}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {previewData?.transformed?.warnings?.length > 0 && (
                  <Box mt={2}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Warnings ({previewData.transformed.warnings.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {previewData.transformed.warnings.map((warning, index) => (
                            <ListItem key={index}>
                              <ListItemText
                                primary={`Row ${warning.row}`}
                                secondary={warning.warnings.join(', ')}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            )}

            <Box mt={3}>
              <Button onClick={() => setActiveStep(2)}>Back</Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={loading || (importMode === 'single' && (previewData?.summary?.totalRows || previewData?.totalRows || 0) === 0)}
                sx={{ ml: 1 }}
              >
                {loading ? 'Importing...' : importMode === 'universal' ? 'Execute Universal Import' : 'Execute Import'}
              </Button>
            </Box>

            {/* Import Progress Section */}
            {loading && (
              <Box mt={3} p={2} border={1} borderColor="divider" borderRadius={1}>
                <Typography variant="h6" gutterBottom>
                  Import Progress
                </Typography>

                <Box display="flex" alignItems="center" mb={2}>
                  <Box width="100%" mr={1}>
                    <LinearProgress variant="determinate" value={importProgress} />
                  </Box>
                  <Box minWidth={35}>
                    <Typography variant="body2" color="textSecondary">
                      {`${importProgress}%`}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body1" gutterBottom>
                  {importStatus}
                </Typography>

                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    Time Elapsed: {formatTime(timeElapsed)}
                  </Typography>
                  {estimatedTimeRemaining !== null && (
                    <Typography variant="body2" color="textSecondary">
                      Est. Remaining: {formatTime(estimatedTimeRemaining)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 4:
        return (
          <Box textAlign="center">
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Import Completed Successfully!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Your data has been imported and is ready to use.
            </Typography>
            <Box mt={3}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Import Another File
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Import Wizard
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper sx={{ p: 3 }}>
        {renderStepContent(activeStep)}
      </Paper>

      {/* Edit Mapping Dialog */}
      <Dialog open={!!editMapping} onClose={() => setEditMapping(null)}>
        <DialogTitle>Edit Field Mapping</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Target Field"
            fullWidth
            value={customMapping}
            onChange={(e) => setCustomMapping(e.target.value)}
            helperText="Enter the field name to map to (e.g., customer_name, item_name)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMapping(null)}>Cancel</Button>
          <Button onClick={handleSaveMapping}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataImport;